// src/pages/SsoPage.js
// Aterrizaje del SSO: llega /sso?t=<ticket> desde eventos.ateneo.co, se canjea el
// ticket contra el backend y, si todo va bien, la sesion queda abierta sin que la
// persona escriba nada.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Wrap = styled.div`
  min-height: 70vh; display: flex; align-items: center; justify-content: center;
  padding: 2rem 1.2rem;
`;
const Card = styled.div`
  max-width: 480px; width: 100%; text-align: center;
  background: #fff; border-radius: 16px; padding: 2.5rem 2rem;
  box-shadow: 0 8px 30px rgba(15, 53, 91, 0.10);
`;
const girar = keyframes`to { transform: rotate(360deg); }`;
const Spinner = styled.div`
  width: 42px; height: 42px; margin: 0 auto 1.2rem;
  border: 3px solid #e2e8f0; border-top-color: #14b8a6; border-radius: 50%;
  animation: ${girar} 0.8s linear infinite;
`;
const Titulo = styled.h2`margin: 0 0 .6rem; color: #0f355b; font-size: 1.25rem;`;
const Texto = styled.p`margin: 0; color: #64748b; line-height: 1.5; font-size: .95rem;`;
const Boton = styled.button`
  margin-top: 1.4rem; padding: .7rem 1.4rem; border: 0; border-radius: 9px;
  background: #14b8a6; color: #fff; font-weight: 600; cursor: pointer;
  &:hover { opacity: .9; }
`;
const BotonSec = styled(Boton)`background: transparent; color: #64748b; border: 1px solid #cbd5e1;`;

const SsoPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [estado, setEstado] = useState('cargando'); // cargando | error
  const [mensaje, setMensaje] = useState('');
  const [volverUrl, setVolverUrl] = useState('');

  // StrictMode monta dos veces en desarrollo. Sin esto el ticket se canjearia dos
  // veces y el segundo intento fallaria siempre (es de un solo uso).
  const yaCanjeado = useRef(false);

  useEffect(() => {
    const ticket = params.get('t');
    const volver = params.get('volver') || '';
    setVolverUrl(volver);

    // Fuera de la URL cuanto antes: lo que queda en la barra acaba en el historial
    // y en la cabecera Referer de cualquier recurso que cargue la pagina.
    if (ticket) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    if (!ticket) {
      setEstado('error');
      setMensaje('El enlace no trae un pase de acceso. Vuelve al sitio anterior e intentalo de nuevo.');
      return;
    }

    if (yaCanjeado.current) return;
    yaCanjeado.current = true;

    (async () => {
      try {
        const { data } = await axios.post(`${API_BASE_URL}/sso.php`, { ticket });
        // La URL de retorno la dicta el backend (viene de su .env). Guardarla aqui es
        // lo que permite que al cerrar sesion la persona vuelva de donde vino.
        if (data.volver) {
          localStorage.setItem('sso_volver', data.volver);
        }
        login({ user: data.user, token: data.token });
        navigate('/', { replace: true });
      } catch (err) {
        const res = err.response?.data;
        setEstado('error');
        setMensaje(
          res?.message ||
          'No pudimos validar tu acceso. Vuelve al sitio anterior e intentalo de nuevo.'
        );
      }
    })();
  }, [params, login, navigate]);

  if (estado === 'cargando') {
    return (
      <Wrap>
        <Card>
          <Spinner />
          <Titulo>Entrando…</Titulo>
          <Texto>Estamos validando tu acceso. Esto toma un segundo.</Texto>
        </Card>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <Card>
        <Titulo>No pudimos abrir tu sesion</Titulo>
        <Texto>{mensaje}</Texto>
        <div>
          <Boton onClick={() => navigate('/login')}>Iniciar sesion aqui</Boton>
          {volverUrl && (
            <BotonSec style={{ marginLeft: 8 }} onClick={() => { window.location.href = volverUrl; }}>
              Volver
            </BotonSec>
          )}
        </div>
      </Card>
    </Wrap>
  );
};

export default SsoPage;
